import { userTable } from '@digitale-verhuurders-verklaring/rds/src/schema/user.sql';
import { createSelectSchema } from 'drizzle-zod';
import { z } from 'zod';

import { locationSchema, locationWithTeamsSchema } from './location';
import { roleSchema } from './role';
import { teamSchema } from './team';

export const userSchema = createSelectSchema(userTable);
export const userDetailSchema = userSchema.merge(
  z.object({
    roles: z.array(roleSchema),
    locations: z.array(locationWithTeamsSchema).optional(),
    teams: z.array(teamSchema).optional(),
    organisationId: z.string().optional(),
  }),
);

export const userInsertSchema = userSchema
  .pick({
    firstName: true,
    lastName: true,
    email: true,
  })
  .merge(
    z.object({
      cognitoId: z.string().nullish(),
      roleIds: z.array(z.string()),
      locationIds: z.array(z.string()),
      teamIds: z.array(z.string()),
    }),
  );

export const userInsertRequestSchema = userInsertSchema.omit({
  cognitoId: true,
});

export const userUpdateSchema = userSchema
  .pick({
    id: true,
    cognitoId: true,
    firstName: true,
    lastName: true,
    email: true,
  })
  .merge(
    z.object({
      roleIds: z.array(z.string()),
      locationIds: z.array(z.string()),
      teamIds: z.array(z.string()),
    }),
  );

export type User = z.infer<typeof userSchema>;
export type UserDetail = z.infer<typeof userDetailSchema>;
export type UserInsert = z.infer<typeof userInsertSchema>;
export type UserInsertRequest = z.infer<typeof userInsertRequestSchema>;
export type UserUpdate = z.infer<typeof userUpdateSchema>;

export const userCompleteSchema = userSchema.merge(
  z.object({
    locations: z.array(locationSchema),
    teams: z.array(teamSchema),
    roles: z.array(roleSchema),
  }),
);

export type UserComplete = z.infer<typeof userCompleteSchema>;

export const userModalDataSchema = z.union([
  z.object({
    type: z.literal('add'),
    data: z.object({}).nullish(),
  }),
  z.object({
    type: z.literal('edit'),
    data: userUpdateSchema,
  }),
]);

export type UserModalData = z.infer<typeof userModalDataSchema>;

/** API response shape for GET /handlers/getCurrentUser */
export const currentUserDtoSchema = z.object({
  id: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  roles: z.array(z.string()),
  organisationId: z.string().nullable(),
});

export type CurrentUserDto = z.infer<typeof currentUserDtoSchema>;
